import { AuthProviderEnum, TProfile } from '@onesource/schemas';
import { useMutation } from '@tanstack/react-query';

import axios from '../axios';
import { setActiveAuthProvider } from './auth-provider-utils';
import { setSession } from './session';

type TValidateOttResponse = {
  access_token: string;
  refresh_token: string;
  user: { email: string };
};

export const useValidateOttMutation = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await axios.post<TValidateOttResponse>('/auth/validate-sso', { token }, { timeout: 30000 });
      return data;
    },
  });
};

export const useConsumeOttMutation = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      await axios.post('/auth/consume-ott', { token });
    },
    retry: 2,
    retryDelay: 2000,
  });
};

/**
 * Shared OTT SSO handler — validates OTT with retry, sets session, and consumes OTT.
 * Returns the validated data so the caller can handle the redirect.
 */
export const useOttSsoHandler = () => {
  const validateOtt = useValidateOttMutation();
  const consumeOtt = useConsumeOttMutation();

  const handleOttSso = async (token: string) => {
    const data = await validateOtt.mutateAsync(token);

    setSession({
      token: data.access_token,
      refreshToken: data.refresh_token,
      user: {
        email: data.user.email || '',
        name: data.user.email?.split('@')[0] || 'User',
      },
      authProvider: AuthProviderEnum.supabase,
    });

    setActiveAuthProvider(AuthProviderEnum.supabase);

    // Consume OTT fire-and-forget (don't block redirect)
    consumeOtt.mutate(token);

    return data;
  };

  return { handleOttSso, isPending: validateOtt.isPending };
};

export const useLegacySsoCheckMutation = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await axios.get<TProfile>('/auth/auth-check', {
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return { data, token };
    },
  });
};
